import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from '../../hooks/useRouterCompat';
import { Trans, t } from '@lingui/macro';
import { Menu as MenuIcon, LogOut, Users, PlusCircle, LayoutGrid, User, Calendar, Hash, Home, PanelRight, Loader2 } from 'lucide-react';
import { MyNotifs } from '../../services/MyNotifs';

import { useAppUIContext } from '../../context/AppUIContext';
import { RoomSimplified } from '../../models/Room';
import { Workspace } from '../../models/Workspace';
import { Fire } from '../../services/FireClient';
import { formatBytes } from '../../services/StorageQuotaService';
import { My } from '../../services/My';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { UtilApp } from '../../services/Utils/UtilApp';
import { useResponsive } from '../../hooks/useResponsive';
import { cn } from '../../lib/utils';

import CreateRoomModal from '../components/CreateRoomModal';
import WSAdminRoomsPage from './admin/WSAdminRoomsPage';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { useToast } from '../../components/ui/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { AdminView, WSAdminSidebarContent } from './admin/WSAdminSidebarContent';
import WSAdminSettings from './admin/WSAdminSettings';
import WSAdminMembers from './admin/WSAdminMembers';
import WSAdminStorage from './admin/WSAdminStorage';
import WSAdminInquiry from './admin/WSAdminInquiry';
import WSAdminCompanyPage from './admin/WSAdminCompanyPage';

const WorkspaceViewPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { workspaces, setIsMobileMenuOpen } = useAppUIContext();
  const [counter, setCounter] = useState(0);
  // Initialize workspace from cache if available to avoid flickering
  const [workspace, setWorkspace] = useState<Workspace | undefined>(() => workspaces.find(w => w.id === workspaceId));
  const [rooms, setRooms] = useState<RoomSimplified[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const history = useHistory();
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const { isMobileOrTablet } = useResponsive();
  const [sheetOpen, setSheetOpen] = useState(false);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const viewParam = query.get('view') as AdminView;
  const [currentView, setCurrentView] = useState<AdminView>(viewParam || 'overview');

  useEffect(() => {
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((_) => {
      setCounter(MyNotifs.NrOfNew);
    });
    return () => { unsubNotifs(); }
  }, []);

  // Update URL when view changes
  const handleViewChange = (view: AdminView) => {
    setCurrentView(view);
    history.push(`/ws/${workspaceId}?view=${view}`);
  };

  // Sync state with URL if it changes externally (e.g. back button or workspace switch)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view') as AdminView;
    const targetView = view || 'overview';
    if (targetView !== currentView) {
      setCurrentView(targetView);
    }
  }, [location.search]);

  // Alert State
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: React.ReactNode;
    description?: React.ReactNode;
    confirmText?: string;
    onConfirm?: () => void;
  }>({ open: false, title: "" });

  useEffect(() => {
    reload();
  }, [workspaceId]);

  async function reload(silent = false) {
    // Don't reset workspace to undefined - keep cached version to prevent flickering
    if (!silent) {
      setRooms([]);
      setInProgress(true);
    } else {
      setIsRefreshing(true);
    }
    // setCurrentView('overview'); // Don't reset view on reload to preserve deep link

    try {
      const res = await Fire.ws.getWorkspaceRooms({ workspaceId: workspaceId || "" });
      if (res.status === 'NOT_MEMBER') {
        setAlertConfig({
          open: true,
          title: t`You are not a member of ${workspaceId}`,
          onConfirm: () => history.replace('/')
        });
        return;
      }

      const excludedIds = ["bffe53c19384a6427b6e", "9261e19779d199e7d703", "835a2e28c75ca0f92942", "e0fb2dd50271c1022e38", "ddd34cbd961453a61cec", "693af880a4a133c558b3", "e7ab45c0cbe47d7517a6", "ede2c52a1e63aa49de3f", "5ab8144fdd3d3a384be6"];
      res.rooms = res.rooms.filter(r => !excludedIds.includes(r.id));
      setRooms(res.rooms.sort((a, b) => (a.created ?? 0) < (b.created ?? 0) ? 1 : -1));

      // Always fetch fresh workspace data from Firestore instead of relying on potentially stale context
      const foundWs = await Fire.ws.getWorkspace(workspaceId || "");
      if (foundWs) setWorkspace(foundWs);

      // Security check: If not owner but trying to access admin view, redirect to overview
      const ownerCheck = foundWs?.owner.id === My.user.id;
      const params = new URLSearchParams(window.location.search);
      const currentViewInUrl = params.get('view') as AdminView;

      if (!ownerCheck && currentViewInUrl && currentViewInUrl !== 'overview') {
        setCurrentView('overview');
        history.replace(`/ws/${workspaceId}`);
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Failed to load workspace" });
    } finally {
      setInProgress(false);
      setIsRefreshing(false);
    }
  }

  const handleJoinClick = (room: RoomSimplified) => {
    if (hasJoined(room.id)) {
      toast({ title: t`You have already joined this room` });
    } else {
      setAlertConfig({
        open: true,
        title: t`Would you like to join this room?`,
        confirmText: t`Join`,
        onConfirm: () => confirmJoin(room)
      });
    }
  }

  const confirmJoin = async (room: RoomSimplified) => {
    setInProgress(true);
    try {
      const res = await Fire.ws.joinRoomViaWorkspace({ roomId: room.id });

      if (res.status === 'NOT_MEMBER') {
        toast({ title: t`You are not a member of this workspace`, duration: 3000 });
      } else if (res.status === 'SUCCESS') {
        history.push(`/room/${room.id}`);
      } else {
        toast({ title: `Error joining room`, duration: 3000 });
      }
    } catch (e) {
      toast({ title: `Error joining room` });
    } finally {
      setInProgress(false);
      setAlertConfig(prev => ({ ...prev, open: false }));
    }
  }

  function hasJoined(roomId: string) {
    return My.Rooms.has(roomId);
  }

  const handleLeaveWorkspace = () => {
    if (!workspace?.id) return;
    setAlertConfig({
      open: true,
      title: t`Would you like leave this workspace?`,
      confirmText: t`Leave now`,
      onConfirm: async () => {
        setInProgress(true);
        try {
          await Fire.ws.leaveWorkspace(workspace.id!, My.user.id);
          const roomid = LocalStorage.get("last-visited-room")
          if (roomid) history.replace(`/room/${roomid}`);
          else history.replace('/main');
        } catch (e) {
          toast({ title: "Failed to leave workspace" });
        } finally {
          setInProgress(false);
          setAlertConfig(prev => ({ ...prev, open: false }));
        }
      }
    });
  }

  const isOwner = workspace?.owner.id === My.user.id;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 z-40 md:rounded-panel bg-white border-b min-w-0 flex items-center justify-between px-4 py-4 shrink-0 shadow-sm relative overflow-hidden">
        <div className="flex items-center gap-2 w-full">
          <div className="shrink-0 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-cy='menu'
              onClick={() => setIsMobileMenuOpen(true)}
              className="relative"
            >
              <MenuIcon className="h-5 w-5" />
              {!!counter && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-[10px]">
                  {counter}
                </Badge>
              )}
            </Button>
          </div>

          <div className="flex flex-1 items-center justify-between min-w-0">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold truncate"><Trans>Workspace</Trans></h1>
              {workspace && (
                <p className="flex items-center text-sm text-gray-600 truncate mt-0.5 gap-2">
                  <Users className="w-4 h-4 text-gray-400" />{workspace.name}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {workspace && !isOwner && (
                <Button variant="ghost" size="sm" onClick={handleLeaveWorkspace}>
                  <LogOut className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline"><Trans>Leave</Trans></span>
                </Button>
              )}
              {/* Owner Admin Menu Trigger for Mobile */}
              {workspace && isOwner && isMobileOrTablet && (
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="icon-box">
                      <PanelRight className="w-5 h-5 text-orange-500" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px] p-0">
                    <WSAdminSidebarContent
                      workspace={workspace}
                      currentView={currentView}
                      onChangeView={handleViewChange}
                      onClose={() => setSheetOpen(false)}
                    />
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </div>
        </div>

        {/* Top Progress Bar (Silent refresh) */}
        {isRefreshing && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-blue-50 overflow-hidden z-10">
            <div className="h-full bg-blue-500 animate-shimmer w-full" style={{
              backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite'
            }} />
          </div>
        )}
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <main className={cn("flex-1 overflow-y-auto flex items-start justify-center")}>
          {inProgress ? (
            <div className="flex flex-col items-center justify-center py-20 w-full h-full">
              <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
              <p className="text-gray-500 font-medium"><Trans>Loading workspace information...</Trans></p>
            </div>
          ) : (
            workspace && (
              <>
                {currentView === 'overview' && (
                  <div className="p-4 md:p-6 lg:p-8 w-full max-w-7xl space-y-8 bg-white md:my-6 md:mx-4 md:rounded-panel shadow-sm md:border border-gray-200">
                    {/* About Workspace Card */}
                    <Card className="border-gray-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <LayoutGrid className="w-5 h-5 text-gray-400" />
                          <h3 className="text-lg font-bold text-gray-900"><Trans>About Workspace</Trans></h3>
                        </div>

                        <div className="space-y-3">
                          {/* Owner */}
                          <div className="flex items-start gap-3">
                            <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-900">{workspace.owner.name}</p>
                                <Badge variant="outline" className="text-xs h-5">
                                  <Trans>Owner</Trans>
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{workspace.owner.email}</p>
                            </div>
                          </div>

                          {/* Created Date */}
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-600">
                                <Trans>Created</Trans>: {UtilApp.dateStrToYmd(workspace.created, My.user.lang)}
                              </p>
                            </div>
                          </div>

                          {/* ID & Tier */}
                          <div className="flex items-center gap-3">
                            <Hash className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <p className="text-sm text-gray-600 font-mono">{workspace.id}</p>
                              {workspace.tier && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {workspace.tier}
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Description */}
                          {workspace.description?.trim() && (
                            <div className="pt-3 border-t">
                              <div className="prose prose-sm max-w-none text-gray-600">
                                <div dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(workspace.description, My.user.id) }} />
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
                          <Home className="w-5 h-5 text-gray-400" />
                          <Trans>Listed Rooms</Trans>
                        </h2>
                        <Button onClick={() => setShowCreateRoomModal(true)}>
                          <PlusCircle className="w-5 h-5 mr-2" />
                          <Trans>New Room</Trans>
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {rooms.filter(r => r.visibility === 'listed').map(room => {
                          const creator = room.users?.find(u => u.id === room.creatorId);
                          return (
                            <Card key={room.id} className="group hover:shadow-lg transition-all duration-200 border-gray-200 flex flex-col h-full">
                              <CardContent className="p-5 flex flex-col flex-1 gap-4">
                                <div className="flex items-start gap-4">
                                  {room.profileImageUrl ? (
                                    <Avatar className="w-14 h-14 rounded-2xl border-2 border-white shadow-sm shrink-0">
                                      <AvatarImage src={room.profileImageUrl} className="object-cover" />
                                      <AvatarFallback className="text-lg bg-gray-100">{room.name.substring(0, 2)}</AvatarFallback>
                                    </Avatar>
                                  ) : (
                                    <Avatar className="w-14 h-14 border-2 border-transparent shrink-0 rounded-2xl">
                                      <AvatarFallback className="text-xl font-bold bg-primary/10 text-primary">
                                        {room.name.substring(0, 1).toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-lg text-gray-900 truncate leading-tight mb-2"># {room.name}</h3>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                      {creator && (
                                        <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">
                                          <Avatar className="w-4 h-4">
                                            <AvatarImage src={creator.photoUrl ?? undefined} />
                                            <AvatarFallback className="text-[9px]">{creator.name[0]}</AvatarFallback>
                                          </Avatar>
                                          <span className="font-medium text-gray-700 max-w-[80px] truncate">{creator.name}</span>
                                        </div>
                                      )}
                                      <span className="text-gray-300">•</span>
                                      <span>{UtilApp.dateStrToDay(room.created, My.user.lang)}</span>
                                      {(room.storageUsage && room.storageUsage > 0) && (
                                        <>
                                          <span className="text-gray-300">•</span>
                                          <span className="font-medium text-orange-600">{formatBytes(room.storageUsage)}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {room.description && (
                                  <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5em]">
                                    {room.description}
                                  </p>
                                )}
                                <div className="flex flex-col gap-3 pt-4 border-t mt-auto">
                                  <div className="flex items-center gap-3">
                                    <div className="flex -space-x-2 overflow-hidden items-center hover:space-x-1 transition-all">
                                      {room.users?.slice(0, 5).map(u => (
                                        <Avatar key={u.id} className="w-7 h-7 border-2 border-white ring-1 ring-gray-100 transition-transform hover:scale-110 hover:z-10 bg-white">
                                          <AvatarImage src={u.photoUrl ?? undefined} />
                                          <AvatarFallback className="text-[10px]">{u.name.substring(0, 1)}</AvatarFallback>
                                        </Avatar>
                                      ))}
                                      {room.users && room.users.length > 5 && (
                                        <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-white bg-gray-100 text-[10px] font-semibold text-gray-600 ring-1 ring-gray-100 z-10 text-gray-500">
                                          +{room.users.length - 5}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-600 gap-1.5" title={t`Total Members`}>
                                      <User className="w-3 h-3 text-gray-500" />
                                      {room.users.length}
                                    </div>
                                  </div>

                                  <Button
                                    size="sm"
                                    variant={hasJoined(room.id) ? "secondary" : "default"}
                                    disabled={hasJoined(room.id)}
                                    onClick={() => handleJoinClick(room)}
                                    className="w-full h-8"
                                  >
                                    {hasJoined(room.id) ? <Trans>Joined</Trans> : <Trans>Join</Trans>}
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )
                        })}
                      </div>

                      {rooms.length === 0 && (
                        <div className="text-center py-16 text-muted-foreground bg-white rounded-xl border border-dashed">
                          <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium"><Trans>This workspace has no rooms.</Trans></p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Admin Views - Only for owners */}
                {isOwner && (
                  <>
                    {currentView === 'rooms' && (
                      <WSAdminRoomsPage />
                    )}
                    {currentView === 'settings' && (
                      <WSAdminSettings workspace={workspace} onUpdate={reload} />
                    )}
                    {currentView === 'members' && (
                      <WSAdminMembers workspace={workspace} />
                    )}
                    {currentView === 'storage' && (
                      <WSAdminStorage workspace={workspace} />
                    )}
                    {currentView === 'inquiry' && (
                      <WSAdminInquiry workspace={workspace} />
                    )}
                    {currentView === 'company' && (
                      <WSAdminCompanyPage workspace={workspace} onUpdate={reload} />
                    )}
                  </>
                )}
              </>
            )
          )}
        </main>

        {/* Right Sidebar - Logic: Show only if owner AND Desktop */}
        {workspace && isOwner && !isMobileOrTablet && (
          <aside className="w-64 flex flex-col shrink-0 my-6">
            <WSAdminSidebarContent
              workspace={workspace}
              currentView={currentView}
              onChangeView={handleViewChange}
            />
          </aside>
        )}
      </div>

      <CreateRoomModal show={showCreateRoomModal} setShow={setShowCreateRoomModal} workspaceId={workspaceId || null} />

      <AlertDialog open={alertConfig.open} onOpenChange={(open) => { if (!open) setAlertConfig(prev => ({ ...prev, open: false })) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            {alertConfig.description && <AlertDialogDescription>{alertConfig.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAlertConfig(prev => ({ ...prev, open: false }))}><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={() => alertConfig.onConfirm?.()}>{alertConfig.confirmText || <Trans>Confirm</Trans>}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default WorkspaceViewPage;
